import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi'; // Fixed import
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import Badge from '../components/ui/Badge';
import { Plus, Download, FileText, Trash2, Clock, Printer, SlidersHorizontal, ChevronUp } from 'lucide-react';
import { formatDate } from '../utils/helpers';
import { exportToPDF, exportToExcel } from '../utils/exportUtils';
import { fetchAllPaginatedRows } from '../utils/paginatedExport';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Input from '../components/ui/Input';
import { useAuth } from '../context/AuthContext';
import { useUIStore } from '../store/useUIStore';

const DispatchList = () => {
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [visibleDispatchColumns, setVisibleDispatchColumns] = useState([]);
  const [tableDispatchColumnWidths, setTableDispatchColumnWidths] = useState({});
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    dispatchNumber: '',
    subject: '',
    dateIssued: '',
    recipient: '',
    modeOfDispatch: '',
    priority: '',
    remarks: ''
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: () => {}
  });
  const { get, del } = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tableDensity, setTableDensity } = useUIStore();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchDispatches();
  }, [page, debouncedSearch, sortConfig, dateRange]);

  const fetchDispatches = async () => {
    setLoading(true);
    try {
      const { from, to } = dateRange;
      const response = await get(`/dispatch?page=${page}&limit=10&search=${debouncedSearch}&sortBy=${sortConfig.key}&order=${sortConfig.direction.toUpperCase()}&dateFrom=${from}&dateTo=${to}`);
      setDispatches(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch dispatches', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const columns = [
    { 
      header: 'Dispatch No.', 
      accessorKey: 'dispatchNumber', 
      className: 'w-40',
      cellClassName: 'w-40',
      render: (row) => <span className="font-medium text-gray-900">{row.dispatchNumber}</span>,
      sortable: true 
    },
    {
      header: 'Subject',
      accessorKey: 'subject',
      className: 'w-[16rem] sm:w-[18rem] lg:w-[22rem]',
      cellClassName: 'w-[16rem] sm:w-[18rem] lg:w-[22rem]',
      noWrap: false,
      sortable: true,
      render: (row) => (
        <span className="block leading-snug break-words [overflow-wrap:anywhere]" title={row.subject || ''}>
          {row.subject || '-'}
        </span>
      )
    },
    { header: 'Date', accessorKey: 'dateIssued', className: 'w-32', cellClassName: 'w-32', render: (row) => formatDate(row.dateIssued), sortable: true },
    { header: 'To', accessorKey: 'recipient', className: 'w-44', cellClassName: 'w-44', sortable: true },
    { header: 'Mode', accessorKey: 'modeOfDispatch', className: 'hidden lg:table-cell w-32', cellClassName: 'hidden lg:table-cell w-32', sortable: true },
    {
      header: 'Remarks',
      accessorKey: 'remarks',
      className: 'hidden 2xl:table-cell 2xl:w-[18rem]',
      cellClassName: 'hidden 2xl:table-cell 2xl:w-[18rem]',
      noWrap: false,
      render: (row) => <span className="block text-sm text-gray-600 leading-snug break-words [overflow-wrap:anywhere]" title={row.remarks || ''}>{row.remarks || '-'}</span>
    },
    { header: 'Priority', accessorKey: 'priority', className: 'w-28', cellClassName: 'w-28', render: (row) => <Badge priority={row.priority}>{row.priority}</Badge>, sortable: true },
    { 
      header: 'Actions', 
      accessorKey: 'actions', 
      className: 'w-32',
      cellClassName: 'w-32',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Button variant="link" onClick={(e) => { e.stopPropagation(); navigate(`/dispatch/${row.id}/view`); }}>
            View
          </Button>
          <Button 
            variant="ghost" 
            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 h-auto"
            onClick={(e) => { 
              e.stopPropagation(); 
              handleDeleteClick(row); 
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    },
  ];

  const filteredDispatches = (Array.isArray(dispatches) ? dispatches : []).filter((row) => {
    const matches = (key, value) => {
      const q = String(value || '').trim().toLowerCase();
      if (!q) return true;
      const v = String(row?.[key] ?? '').toLowerCase();
      return v.includes(q);
    };

    const dateQ = String(columnFilters.dateIssued || '').trim();
    const dateV = row?.dateIssued ? String(row.dateIssued).substring(0, 10) : '';

    return (
      matches('dispatchNumber', columnFilters.dispatchNumber) &&
      matches('subject', columnFilters.subject) &&
      matches('recipient', columnFilters.recipient) &&
      matches('modeOfDispatch', columnFilters.modeOfDispatch) &&
      matches('priority', columnFilters.priority) &&
      matches('remarks', columnFilters.remarks) &&
      (!dateQ || dateV.includes(dateQ))
    );
  });

  const applyColumnFilters = (rows) => (Array.isArray(rows) ? rows : []).filter((row) => {
    const matches = (key, value) => {
      const q = String(value || '').trim().toLowerCase();
      if (!q) return true;
      const v = String(row?.[key] ?? '').toLowerCase();
      return v.includes(q);
    };

    const dateQ = String(columnFilters.dateIssued || '').trim();
    const dateV = row?.dateIssued ? String(row.dateIssued).substring(0, 10) : '';

    return (
      matches('dispatchNumber', columnFilters.dispatchNumber) &&
      matches('subject', columnFilters.subject) &&
      matches('recipient', columnFilters.recipient) &&
      matches('modeOfDispatch', columnFilters.modeOfDispatch) &&
      matches('priority', columnFilters.priority) &&
      matches('remarks', columnFilters.remarks) &&
      (!dateQ || dateV.includes(dateQ))
    );
  });

  const fetchAllDispatchesForExport = async () => {
    const { from, to } = dateRange;
    const allRows = await fetchAllPaginatedRows({
      fetchPage: (currentPage) => get(`/dispatch?page=${currentPage}&limit=200&search=${debouncedSearch}&sortBy=${sortConfig.key}&order=${sortConfig.direction.toUpperCase()}&dateFrom=${from}&dateTo=${to}`)
    });

    return applyColumnFilters(allRows);
  };

  const handleDeleteClick = (row) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Dispatch',
      message: `Are you sure you want to delete dispatch ${row.dispatchNumber}? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: () => deleteDispatch(row.id)
    });
  };

  const deleteDispatch = async (id) => {
    try {
      await del(`/dispatch/${id}`);
      toast.success('Dispatch deleted successfully');
      fetchDispatches();
    } catch (error) {
      console.error('Failed to delete dispatch', error);
      toast.error('Failed to delete dispatch');
    }
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const getVisibleColumns = (list = []) => {
    const key = (column) => String(column.header || column.accessorKey || '').trim();
    return list.filter((column) => {
      const match = visibleDispatchColumns.find((item) => item.key === key(column));
      return match ? match.visible : column.visible !== false;
    });
  };

  const getExportColumnWidths = (exportColumns = []) => {
    const widths = {};
    exportColumns.forEach((column, index) => {
      const key = String(column.header || column.accessorKey || '').trim();
      const width = Number(tableDispatchColumnWidths[key] || 0);
      if (width > 0) {
        widths[index] = { wch: Math.min(Math.max(width / 8, 12), 40) };
      }
    });
    return widths;
  };

  const handleExportPDF = async () => {
    try {
      const rows = await fetchAllDispatchesForExport();
      const exportColumns = getVisibleColumns(columns.filter(c => c.accessorKey !== 'actions'));
      await exportToPDF(rows, exportColumns, 'Dispatch Register', {
        startDate: dateRange.from,
        endDate: dateRange.to,
        orientation: 'landscape',
        officeName: user?.officeName || '',
        columnWidths: getExportColumnWidths(exportColumns)
      });
      toast.success('PDF exported successfully');
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  const handlePrintPDF = async () => {
    try {
      const rows = await fetchAllDispatchesForExport();
      const exportColumns = getVisibleColumns(columns.filter(c => c.accessorKey !== 'actions'));
      const result = await exportToPDF(rows, exportColumns, 'Dispatch Register', {
        startDate: dateRange.from,
        endDate: dateRange.to,
        orientation: 'landscape',
        officeName: user?.officeName || '',
        output: 'print',
        columnWidths: getExportColumnWidths(exportColumns)
      });
      if (result?.mode === 'print-fallback-download') {
        toast.success('Popup blocked. PDF downloaded instead.');
      } else {
        toast.success('Print preview opened');
      }
    } catch {
      toast.error('Failed to print');
    }
  };

  const handleExportExcel = async () => {
    const rows = await fetchAllDispatchesForExport();
    const exportColumns = getVisibleColumns(columns.filter(c => c.accessorKey !== 'actions'));
    const workbookRows = rows.map((row) => Object.fromEntries(exportColumns.map((column) => {
      const value = typeof column.accessorKey === 'function' ? column.accessorKey(row) : (row[column.accessorKey] ?? '');
      return [column.header, value];
    })));
    exportToExcel(workbookRows, 'Dispatch_Register', {
      columnWidths: getExportColumnWidths(exportColumns)
    });
    toast.success('Excel exported successfully');
  };

  return (
    <div className="space-y-5">
      <div className="rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-600 via-violet-600 to-blue-600 p-4 text-white shadow-[0_18px_40px_rgba(79,70,229,0.22)] sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-indigo-100">Dispatch workflow</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">Dispatch Register</h1>
            <p className="mt-1 text-sm text-indigo-100/90">Track outgoing correspondence, approvals, and delivery status.</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <div className="inline-flex rounded-xl border border-white/15 bg-white/10 p-1">
              <Button
                size="sm"
                variant={tableDensity === 'comfortable' ? 'primary' : 'ghost'}
                onClick={() => setTableDensity('comfortable')}
                className={tableDensity === 'comfortable' ? 'bg-white text-indigo-700 hover:bg-indigo-50' : 'text-white hover:bg-white/10'}
              >
                Comfortable
              </Button>
              <Button
                size="sm"
                variant={tableDensity === 'compact' ? 'primary' : 'ghost'}
                onClick={() => setTableDensity('compact')}
                className={tableDensity === 'compact' ? 'bg-white text-indigo-700 hover:bg-indigo-50' : 'text-white hover:bg-white/10'}
              >
                Compact
              </Button>
            </div>
            <Button variant="secondary" onClick={handleExportPDF} className="bg-white/10 text-white border-white/15 hover:bg-white/15">
              <FileText className="w-4 h-4 mr-2 text-red-200" /> PDF
            </Button>
            <Button variant="secondary" onClick={handlePrintPDF} className="bg-white/10 text-white border-white/15 hover:bg-white/15">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button variant="secondary" onClick={handleExportExcel} className="bg-white/10 text-white border-white/15 hover:bg-white/15">
              <Download className="w-4 h-4 mr-2 text-green-200" /> Excel
            </Button>
            <Button variant="primary" onClick={() => navigate('/dispatch/new')} className="bg-white text-indigo-700 hover:bg-indigo-50 shadow-sm">
              <Plus className="w-5 h-5 mr-2" />
              New Dispatch
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Total items</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{dispatches.length}</div>
          <div className="mt-1 text-xs text-slate-500">Current page volume</div>
        </div>
        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Registered today</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{dispatches.filter(item => item.dateIssued && item.dateIssued.slice(0, 10) === new Date().toISOString().slice(0, 10)).length}</div>
          <div className="mt-1 text-xs text-slate-500">Live daily count</div>
        </div>
        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Urgents</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{dispatches.filter(item => String(item.priority || '').toLowerCase() === 'urgent').length}</div>
          <div className="mt-1 text-xs text-slate-500">Priority lane</div>
        </div>
      </div>

      <div className="page-section p-3 sm:p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex-1 w-full">
            <SearchInput 
              value={search} 
              onChange={setSearch} 
              onClear={() => setSearch('')}
              placeholder="Search by dispatch number, subject, or recipient..." 
              className="border-slate-200 focus:ring-indigo-500"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:w-auto">
             <div className="w-full md:w-40">
              <Input 
                type="date" 
                label="From"
                value={dateRange.from} 
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="mb-0 border-slate-200 focus:ring-indigo-500"
              />
            </div>
            <div className="w-full md:w-40">
              <Input 
                type="date" 
                label="To"
                value={dateRange.to} 
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="mb-0 border-slate-200 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="page-section p-3 sm:p-4">
        <div className="flex justify-end mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMoreFilters(prev => !prev)}
            className="inline-flex items-center gap-2 rounded-full border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
          >
            {showMoreFilters ? <ChevronUp className="h-3.5 w-3.5" /> : <SlidersHorizontal className="h-3.5 w-3.5" />}
            {showMoreFilters ? 'Hide Filters' : 'More Filters'}
          </Button>
        </div>

        {showMoreFilters && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-7">
            <Input label="Dispatch No." value={columnFilters.dispatchNumber} onChange={(e) => setColumnFilters(p => ({ ...p, dispatchNumber: e.target.value }))} />
            <Input label="Subject" value={columnFilters.subject} onChange={(e) => setColumnFilters(p => ({ ...p, subject: e.target.value }))} />
            <Input label="Date" value={columnFilters.dateIssued} onChange={(e) => setColumnFilters(p => ({ ...p, dateIssued: e.target.value }))} />
            <Input label="To" value={columnFilters.recipient} onChange={(e) => setColumnFilters(p => ({ ...p, recipient: e.target.value }))} />
            <Input label="Mode" value={columnFilters.modeOfDispatch} onChange={(e) => setColumnFilters(p => ({ ...p, modeOfDispatch: e.target.value }))} />
            <Input label="Remarks" value={columnFilters.remarks} onChange={(e) => setColumnFilters(p => ({ ...p, remarks: e.target.value }))} />
            <Input label="Priority" value={columnFilters.priority} onChange={(e) => setColumnFilters(p => ({ ...p, priority: e.target.value }))} />
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.06)]">
        <Table 
          columns={columns} 
          data={filteredDispatches} 
          sortConfig={sortConfig}
          onSort={handleSort}
          tableClassName="table-fixed"
          density={tableDensity}
          enableColumnVisibility
          enableColumnResize
          showColumnControls
          onColumnVisibilityChange={setVisibleDispatchColumns}
          onColumnResizeChange={setTableDispatchColumnWidths}
        />
        <Pagination 
          currentPage={page} 
          totalPages={totalPages} 
          onPageChange={setPage} 
          totalItems={filteredDispatches.length}
          itemsPerPage={10}
        />
      </div>
      
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
      />
    </div>
  );
};

export default DispatchList;
